import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Subjects from "@/pages/Subjects";
import QuizSetup from "@/pages/QuizSetup";
import QuizRunner from "@/pages/QuizRunner";
import QuizResult from "@/pages/QuizResult";
import History from "@/pages/History";
import Bookmarks from "@/pages/Bookmarks";
import Checkout from "@/pages/Checkout";
import Receipt from "@/pages/Receipt";
import { EnquiryProvider } from "@/context/EnquiryContext";
import { FloatingEnquire } from "@/components/EnquireButton";
import Admin from "@/pages/Admin";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import Contact from "@/pages/Contact";
import MyClasses from "@/pages/MyClasses";
import Profile from "@/pages/Profile";
import Analytics from "@/pages/Analytics";

function LandingOrDashboard() {
  const { status, user } = useAuth();
  if (status === "loading") return null;
  if (status === "authenticated") {
    return <Navigate to={user?.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  return <Landing />;
}

export default function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <EnquiryProvider>
          <div className="App flex min-h-screen flex-col bg-background text-foreground">
            <Navbar />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<LandingOrDashboard />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/pricing" element={<Navigate to="/courses" replace />} />
                <Route path="/subscription" element={<Navigate to="/courses" replace />} />
                <Route path="/checkout/:slug" element={<Checkout />} />
                <Route path="/receipt/:id" element={<ProtectedRoute><Receipt /></ProtectedRoute>} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:slug" element={<CourseDetail />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/subjects" element={<ProtectedRoute><Subjects /></ProtectedRoute>} />
                <Route path="/my-courses" element={<ProtectedRoute><MyClasses /></ProtectedRoute>} />
                <Route path="/my-classes" element={<Navigate to="/my-courses" replace />} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/quiz/new" element={<ProtectedRoute><QuizSetup /></ProtectedRoute>} />
                <Route path="/quiz/:id" element={<ProtectedRoute><QuizRunner /></ProtectedRoute>} />
                <Route path="/quiz/:id/result" element={<ProtectedRoute><QuizResult /></ProtectedRoute>} />
                <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
                <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
                <Route path="/bookmarks" element={<ProtectedRoute><Bookmarks /></ProtectedRoute>} />
                <Route path="/revision" element={<Navigate to="/quiz/new?mode=revision" replace />} />
                <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
            <Footer />
            <FloatingEnquire />
            <Toaster position="top-right" />
          </div>
          </EnquiryProvider>
        </BrowserRouter>
      </AuthProvider>
    </SettingsProvider>
  );
}
